// src/pages/Login.jsx
import { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Stack,
  Alert,
  Avatar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";
import { loginErrorMessage } from "./loginErrors";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await login({ email, password: p });
      nav("/project/overview", { replace: true });
    } catch (e) {
      // In the screen's language; the service's own text is English.
      setErr(loginErrorMessage(e, t));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        background: `
          radial-gradient(circle at top left, rgba(0, 92, 175, 0.18), transparent 28%),
          radial-gradient(circle at bottom right, rgba(122, 176, 63, 0.2), transparent 24%),
          linear-gradient(145deg, #eef7ff 0%, #dcefff 48%, #cfe4fb 100%)
        `,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "10% auto auto 8%",
          width: 260,
          height: 260,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0) 70%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          right: "8%",
          bottom: "10%",
          width: 320,
          height: 320,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0) 72%)",
          pointerEvents: "none",
        },
      }}
    >
      <LanguageSwitcher
        sx={{
          position: "absolute",
          zIndex: 2,
          top: { xs: 18, sm: 28 },
          right: { xs: 18, sm: 30 },
          bgcolor: "rgba(255,255,255,0.72)",
        }}
      />
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 460,
          px: { xs: 3, sm: 4.5 },
          py: { xs: 4, sm: 5 },
          borderRadius: 6,
          border: "1px solid rgba(255, 255, 255, 0.65)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,251,255,0.93) 100%)",
          backdropFilter: "blur(18px)",
          boxShadow:
            "0 24px 55px rgba(27, 63, 104, 0.16), 0 6px 18px rgba(27, 63, 104, 0.08)",
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <Avatar
            src="/logo.png"
            alt="MetEn"
            sx={{
              width: 92,
              height: 92,
              p: 0.75,
              bgcolor: "rgba(255,255,255,0.92)",
              boxShadow: "0 12px 30px rgba(0, 92, 175, 0.15)",
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: "#133c6f",
              letterSpacing: "0.04em",
              textShadow: "0 1px 0 rgba(255, 255, 255, 0.7)",
              userSelect: "none",
            }}
          >
            PYTHIA
          </Typography>
          <Stack spacing={0.75} sx={{ width: "100%", textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                color: "#1f3f67",
                fontWeight: 600,
                letterSpacing: "0.02em",
                userSelect: "none",
              }}
            >
              {t("login.platform")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(31, 63, 103, 0.72)",
                maxWidth: 320,
                mx: "auto",
                textWrap: "balance",
                userSelect: "none",
              }}
            >
              {t("login.intro")}
            </Typography>
          </Stack>
          {err && (
            <Alert
              severity="error"
              sx={{
                alignSelf: "stretch",
                borderRadius: 2.5,
                "& .MuiAlert-message": { fontWeight: 500 },
              }}
            >
              {err}
            </Alert>
          )}
          <Box component="form" onSubmit={onSubmit} sx={{ width: "100%" }}>
            <Stack spacing={2.25}>
              <TextField
                label={t("login.email")}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#123f73",
                    fontWeight: 600,
                    WebkitTextFillColor: "#123f73",
                    caretColor: "#0f5fb3",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: "rgba(243, 248, 255, 0.86)",
                    transition: "all 0.2s ease",
                    "& fieldset": {
                      borderColor: "rgba(28, 74, 122, 0.18)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0, 92, 175, 0.35)",
                    },
                    "&.Mui-focused": {
                      bgcolor: "#fff",
                      boxShadow: "0 0 0 4px rgba(0, 92, 175, 0.10)",
                    },
                    "& input:-webkit-autofill": {
                      WebkitTextFillColor: "#123f73",
                      caretColor: "#0f5fb3",
                      borderRadius: "inherit",
                      WebkitBoxShadow:
                        "0 0 0 100px rgba(232, 242, 252, 0.98) inset",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "& input:-webkit-autofill:hover": {
                      WebkitTextFillColor: "#123f73",
                      WebkitBoxShadow:
                        "0 0 0 100px rgba(226, 238, 251, 1) inset",
                    },
                    "& input:-webkit-autofill:focus": {
                      WebkitTextFillColor: "#123f73",
                      caretColor: "#0f5fb3",
                      WebkitBoxShadow:
                        "0 0 0 100px rgba(255, 255, 255, 1) inset",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 600,
                    color: "#2d5d90",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#0f5fb3",
                  },
                }}
              />
              <TextField
                label={t("login.password")}
                type={showPassword ? "text" : "password"}
                value={p}
                onChange={(e) => setP(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#123f73",
                    fontWeight: 600,
                    WebkitTextFillColor: "#123f73",
                    caretColor: "#0f5fb3",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: "rgba(243, 248, 255, 0.86)",
                    transition: "all 0.2s ease",
                    "& fieldset": {
                      borderColor: "rgba(28, 74, 122, 0.18)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0, 92, 175, 0.35)",
                    },
                    "&.Mui-focused": {
                      bgcolor: "#fff",
                      boxShadow: "0 0 0 4px rgba(0, 92, 175, 0.10)",
                    },
                    "& input:-webkit-autofill": {
                      WebkitTextFillColor: "#123f73",
                      caretColor: "#0f5fb3",
                      borderRadius: "inherit",
                      WebkitBoxShadow:
                        "0 0 0 100px rgba(232, 242, 252, 0.98) inset",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "& input:-webkit-autofill:hover": {
                      WebkitTextFillColor: "#123f73",
                      WebkitBoxShadow:
                        "0 0 0 100px rgba(226, 238, 251, 1) inset",
                    },
                    "& input:-webkit-autofill:focus": {
                      WebkitTextFillColor: "#123f73",
                      caretColor: "#0f5fb3",
                      WebkitBoxShadow:
                        "0 0 0 100px rgba(255, 255, 255, 1) inset",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 600,
                    color: "#2d5d90",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#0f5fb3",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? t("login.hidePassword") : t("login.showPassword")
                        }
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        sx={{ color: "rgba(31, 63, 103, 0.78)" }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  mt: 1,
                  py: 1.55,
                  borderRadius: 3,
                  fontSize: "1rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  background: "linear-gradient(135deg, #0f5fb3 0%, #194f97 100%)",
                  boxShadow: "0 12px 24px rgba(15, 95, 179, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0a549f 0%, #143f77 100%)",
                    boxShadow: "0 16px 28px rgba(15, 95, 179, 0.3)",
                  },
                }}
              >
                {t("login.submit")}
              </Button>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  color: "rgba(31, 63, 103, 0.62)",
                  pt: 0.5,
                  letterSpacing: "0.01em",
                }}
              >
                {t("login.secure")}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
